import Breadcrumb from 'components/Navigation/Breadcrumb';
import React, { useEffect, useMemo, useState } from 'react';
import RSAImage from '../../../../../../assets/assets/rsa.png';
import CustomSelect from 'components/Form/CustomSelect';
import TextField from 'components/Form/TextField';
import Button from 'components/Form/Button';
import { Loader } from 'components/Loader';
import AssetAvatar from 'components/Avatar/AssetAvatar';
import AllAssetAvatar from 'components/Avatar/AllAssetAvatar';
import { useGetBeneficiaryList } from 'common/queries-and-mutations/beneficiary';
import AssignedCard from 'components/Cards/AssignedCard';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { Alert } from 'components/Toast';
import {
  useAssignPensionBeneficiary,
  useGetPensionList
} from 'common/queries-and-mutations/pension';
import { PensionAccount } from 'service/dto/pension-account.dto';

export default function AssignPensionBeneficiary() {
  const [selectedAssets, setSelectedAssets] = useState<number[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutate, isSuccess, isError, isLoading } =
    useAssignPensionBeneficiary();

  const { data, isLoading: isStockLoading, refetch } = useGetPensionList();

  const { data: beneficiaries, isLoading: isBeneficiaryLoading } =
    useGetBeneficiaryList();

  const formik = useFormik({
    initialValues: {
      pensionId: null,
      beneficiary: null,
      allottedPercentage: '',
      note: ''
    },
    validationSchema: Yup.object({
      pensionId: Yup.array().required('Please select Asset'),
      allottedPercentage: Yup.string().required('Please enter proportion'),
      beneficiary: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string()
        })
        .required('Please select beneficiary')
    }),
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onSubmit(values);
    }
  });

  const handleSelectAsset = (asset: PensionAccount) => {
    if (!selectedAssets.includes(asset.id!)) {
      const pensionId = [...selectedAssets, asset.id!];
      formik.setFieldValue('pensionId', pensionId);
      setSelectedAssets(pensionId);
    } else {
      const pensionId = selectedAssets.filter(value => value !== asset.id);
      formik.setFieldValue('pensionId', pensionId);
      setSelectedAssets(pensionId);
    }
  };

  const handleSelectAllAsset = () => {
    if (data && data.length === selectedAssets.length) {
      setSelectedAssets([]);
    } else {
      const pensionId = data!.map(asset => asset.id!);
      setSelectedAssets(pensionId);
      formik.setFieldValue('pensionId', pensionId);
    }
  };

  const normalizeResult = useMemo(() => {
    return beneficiaries?.map(beneficiary => ({
      value: beneficiary.id!,
      label: `${beneficiary.beneficiaryFirstName} ${beneficiary.beneficiaryMiddleName} ${beneficiary.beneficiaryLastName}`
    }));
  }, [beneficiaries]);

  const onSubmit = (formValue: any) => {
    const payload = {
      pensionId: formValue.pensionId,
      beneficiaryId: formValue.beneficiary.value,
      allottedPercentage: Number(formValue.allottedPercentage),
      note: formValue.note
    };
    mutate(payload);
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      refetch();
      setSelectedAssets([]);
      toast(
        <Alert message="Beneficiary Successfully Assigned" type="success" />
      );
      setIsSubmitting(false);
      formik.resetForm();
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError]);

  return (
    <div>
      {isStockLoading && isBeneficiaryLoading ? (
        <div className="mt-40 flex flex-col items-center justify-center">
          <Loader color={'#FF6606'} height={50} width={50} />
        </div>
      ) : (
        <>
          <Breadcrumb title="Assign Beneficiary" />

          <div className="grid grid-cols-12 gap-4 pt-5 sm:gap-5 lg:gap-6">
            <div className="col-span-12 flex space-x-11 overflow-x-auto px-[var(--margin-x)] transition-all duration-[.25s] scrollbar-hide">
              {data?.map(asset => {
                return (
                  <div
                    className="w-72 shrink-0 space-y-9 rounded-xl py-4"
                    key={asset.id}
                  >
                    <AssignedCard
                      title={asset.pensionAdministratorName}
                      subtitle={asset.pinNumber}
                      percentage={asset.percentageAlloted}
                      image={RSAImage}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <form
            autoComplete="false"
            className="mt-10"
            onSubmit={formik.handleSubmit}
          >
            <div className="mt-8">
              <div className="rounded bg-white p-7 px-8">
                <p className="text-base font-normal text-[#34475E]">
                  Select Asset
                </p>

                <div className="mt-8 flex items-center gap-9">
                  <button
                    className="flex flex-col items-center gap-1"
                    onClick={handleSelectAllAsset}
                    type="button"
                  >
                    <AllAssetAvatar
                      fillIndicator={
                        data && data.length === selectedAssets.length
                      }
                      image={RSAImage}
                    />
                    <p className="mt-4 text-xs text-[#34475E]">All</p>
                  </button>
                  {data?.map(asset => (
                    <button
                      className="flex flex-col items-center gap-1"
                      key={asset.id}
                      onClick={() => handleSelectAsset(asset)}
                      type="button"
                    >
                      <AssetAvatar
                        fillIndicator={selectedAssets.includes(asset.id!)}
                        image={RSAImage}
                      />
                      <p className="mt-2 text-xs text-[#34475E]">
                        {asset.pensionAdministratorName}
                      </p>
                    </button>
                  ))}
                </div>
                <div className="mt-3 px-10 text-xs capitalize text-red-600">
                  {formik.errors.pensionId && <p>Please select asset</p>}
                </div>

                <div className="mt-8 grid grid-cols-2 gap-4">
                  <CustomSelect
                    options={normalizeResult}
                    selectedOption={formik.values.beneficiary}
                    handleOptionChange={option => {
                      formik.setFieldValue(`beneficiary`, option);
                    }}
                    label="Select Beneficiary"
                    name="beneficiary"
                    isRequired
                    className="rounded-2xl"
                    error={
                      formik.touched.beneficiary
                        ? formik.errors.beneficiary
                          ? 'Please select beneficiary'
                          : ''
                        : ''
                    }
                  />
                  <TextField
                    name="allottedPercentage"
                    placeholder="Proportion (%)"
                    value={formik.values.allottedPercentage}
                    onChange={e => {
                      const inputValue = e.target.value;
                      if (
                        inputValue === '' ||
                        (parseInt(inputValue) >= 0 && !inputValue.includes('-'))
                      ) {
                        formik.handleChange(e);
                      }
                    }}
                    onBlur={e => {
                      const inputValue = e.target.value;
                      if (
                        inputValue === '' ||
                        (parseInt(inputValue) >= 0 && !inputValue.includes('-'))
                      ) {
                        formik.handleBlur(e);
                      }
                    }}
                    label="Proportion (%)"
                    isRequired
                    size="md"
                    className="rounded-lg border-[1.5px] border-gray-300"
                    error={
                      formik.touched.allottedPercentage
                        ? formik.errors.allottedPercentage
                        : ''
                    }
                  />
                  <TextField
                    name="note"
                    placeholder="Note"
                    value={formik.values.note}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Note"
                    size="md"
                    className="rounded-lg border-[1.5px] border-gray-300"
                    error={formik.touched.note ? formik.errors.note : ''}
                  />
                  <div className="flex items-center justify-end">
                    <Button
                      type="submit"
                      variant="primary"
                      size="sm"
                      className="mt-5 w-[9rem] rounded p-[0.5rem] text-base"
                      label="Add Beneficiary"
                      disabled={!formik.isValid}
                      loading={isSubmitting || isLoading}
                    ></Button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
